import { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@mds/shared/lib/utils';
import { useQuery } from '@tanstack/react-query';

import { menuOptionCls, menuPanelCls, useAnchoredPanel } from '@mds/shared/ui/anchored-panel';
import * as customers from '@/customers/customers.api';
import useDebounced from '@mds/shared/hooks/use-debounced';

/** How many matches the panel shows before asking for a narrower search. */
const LIMIT = 20;

/**
 * Pick a customer by typing their name.
 *
 * The directory is too big to hand down a list of, so the search runs on the
 * server — `GET /admin/customers?q=` matches reference, name, email, phone,
 * tags and interests — and only the matches come back. Keystrokes are
 * debounced, so a name is one request rather than one per letter.
 *
 * A chosen customer is shown by name even when they are not in the current
 * matches: the id is resolved on its own, which is what makes a deep link like
 * `?customerId=…` render as a name rather than a UUID.
 *
 * Focus stays on the input and the active option is tracked with
 * `aria-activedescendant`, the same bargain `Select` makes.
 */
export default function CustomerPicker({
  value,
  onChange,
  disabled,
  placeholder = 'Search customers…',
  anyLabel,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  onBlur,
}: {
  /** The chosen customer's id, or `''` for none. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When given, a first row that clears the choice — for filters. */
  anyLabel?: string;
  className?: string;
  'aria-label'?: string;
  /** Set by a form field, so the input announces its hint or error. */
  'aria-describedby'?: string;
  /** Set by a form field whose choice failed validation. */
  'aria-invalid'?: boolean;
  onBlur?: () => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [active, setActive] = useState(0);
  const {
    anchor: root,
    panel: list,
    style,
    gapClass,
  } = useAnchoredPanel<HTMLDivElement, HTMLUListElement>(open);

  const q = useDebounced(typed.trim());

  // The panel is the only thing that needs matches, so nothing is asked for
  // until it is open.
  const matches = useQuery({
    queryKey: ['customers', 'picker', q],
    queryFn: () =>
      customers.listCustomers({
        limit: LIMIT,
        sort: 'name',
        direction: 'asc',
        ...(q ? { q } : {}),
      }),
    enabled: open && !disabled,
    placeholderData: (previous) => previous,
  });

  // The chosen customer, read by id. The list it came from is gone as soon as
  // the search changes, and a deep link never had one.
  const chosen = useQuery({
    queryKey: ['customer', value],
    queryFn: () => customers.getCustomer(value),
    enabled: !!value,
  });

  const rows = useMemo(() => matches.data?.data ?? [], [matches.data]);
  const total = matches.data?.pagination.total ?? 0;
  const chosenName = chosen.data?.name ?? '';

  const options = useMemo(
    () => [
      ...(anyLabel ? [{ value: '', label: anyLabel, hint: '' }] : []),
      ...rows.map((c) => ({ value: c.id, label: c.name, hint: c.reference })),
    ],
    [anyLabel, rows],
  );

  // Closed, the input is a label for what is chosen; open, it is the search box.
  const shown = open ? typed : value ? chosenName : (anyLabel ?? '');

  useEffect(() => {
    setActive(0);
  }, [q, open]);

  // Pointer down rather than click: a click on another control should close
  // this before that control reacts, not after. The panel is portalled out of
  // the input's subtree, so it has to be tested on its own — otherwise picking
  // an option reads as a click on the outside world.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (root.current?.contains(target) || list.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, root, list]);

  // Keep the active option in view when the arrows walk past the panel's edge.
  useEffect(() => {
    if (!open) return;
    list.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active, list]);

  const close = () => {
    setOpen(false);
    setTyped('');
  };

  const pick = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Or the dialog this sits in takes the same Escape and closes too.
      e.stopPropagation();
      return close();
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Tab') return close();
    if (e.key === 'Enter') {
      e.preventDefault();
      return pick(active);
    }
    const last = options.length - 1;
    const to =
      e.key === 'ArrowDown'
        ? Math.min(active + 1, last)
        : e.key === 'ArrowUp'
          ? Math.max(active - 1, 0)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (to === null) return;
    e.preventDefault();
    setActive(to);
  };

  return (
    <div ref={root} className={cn('relative', className)}>
      <div className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          aria-activedescendant={open ? `${id}-${active}` : undefined}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          autoComplete="off"
          disabled={disabled}
          value={shown}
          placeholder={placeholder}
          onChange={(e) => {
            setTyped(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={cn(
            'h-(--target-control) w-full rounded-xl border border-ink/25 bg-surface pr-11 pl-4 text-base text-ink transition-colors placeholder:text-ink-muted',
            'focus:border-brand-edge focus:outline-(length:--focus-ring-width) focus:outline-offset-(--focus-ring-offset) focus:outline-(color:--focus-ring-color)',
            'aria-invalid:border-destructive aria-invalid:bg-danger-wash',
            'disabled:cursor-not-allowed disabled:opacity-55',
          )}
        />
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-ink-muted transition-transform',
            open && 'rotate-180',
          )}
        />
      </div>

      {open &&
        createPortal(
          <ul
            ref={list}
            id={id}
            role="listbox"
            aria-label={ariaLabel}
            style={style}
            className={cn(menuPanelCls, gapClass)}
          >
            {matches.isPending ? (
              <li className="px-3 py-2.5 text-base text-ink-muted">Searching…</li>
            ) : matches.isError ? (
              <li className="px-3 py-2.5 text-base text-warn-text">
                The customer directory could not be searched.
              </li>
            ) : options.length === 0 ? (
              <li className="px-3 py-2.5 text-base text-ink-muted">
                {q ? `No customer matches “${q}”.` : 'No customers yet.'}
              </li>
            ) : (
              <>
                {options.map((option, i) => {
                  const isSelected = option.value === value;
                  return (
                    <li
                      key={option.value || 'any'}
                      id={`${id}-${i}`}
                      role="option"
                      aria-selected={isSelected}
                      onPointerEnter={() => setActive(i)}
                      onClick={() => pick(i)}
                      className={cn(
                        menuOptionCls,
                        i === active && 'bg-menu-option-active-bg text-menu-option-active-fg',
                        isSelected && 'bg-menu-option-selected-bg text-menu-option-selected-fg',
                      )}
                    >
                      <span className="min-w-0 truncate">{option.label}</span>
                      {option.hint && (
                        <span className="shrink-0 font-mono text-sm text-ink-muted">
                          {option.hint}
                        </span>
                      )}
                    </li>
                  );
                })}
                {total > rows.length && (
                  <li className="px-3 py-2.5 text-sm text-ink-muted">
                    Showing {rows.length} of {total}. Keep typing to narrow it.
                  </li>
                )}
              </>
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
}
